/**
 * Cross-app URL *shapes* — pure, toolchain-agnostic.
 *
 * This module deliberately touches NO environment variables, so it is safe to
 * import from Vite (console, internal), Astro (website) AND webpack/Next
 * (hosted). The env-bound wrappers live next to their toolchain:
 *
 *   - Vite / Astro → `@knitknot/shared/lib/appUrls`  (reads `import.meta.env`)
 *   - Next.js      → `apps/hosted/lib/urls.ts`       (reads `process.env`)
 *
 * Both of those import the constants + rules below, so there is exactly one
 * definition of "what a hosted workspace URL looks like" in the repo.
 */

// ---------------------------------------------------------------------------
// Production origins — the defaults every app falls back to when no env var is
// set. A plain `npm run build` with no env therefore still emits real links.
// ---------------------------------------------------------------------------

/** Console SPA. */
export const PROD_CONSOLE_URL = 'https://app.knitknot.ai';
/** Hosted report app. Wildcard-subdomain hosting: `{workspace}.knitknot.io`. */
export const PROD_HOSTED_URL = 'https://knitknot.io';
/** Marketing site. */
export const PROD_WEBSITE_URL = 'https://knitknot.ai';
/** Backend API. */
export const PROD_API_URL = 'https://api.knitknot.ai';
/** MCP Streamable HTTP endpoint (trailing slash is part of the path). */
export const PROD_MCP_URL = 'https://mcp.knitknot.ai/mcp/';

// ---------------------------------------------------------------------------
// Dual-bundler API base
// ---------------------------------------------------------------------------

/**
 * The backend origin, for the handful of shared components that render under
 * BOTH bundlers (`DataPanel`, `ReachOutDialog` — the console previews a report
 * that hosted also serves) and therefore can't touch `import.meta.env`:
 * webpack rewrites an unknown `import.meta.env` to `undefined` and the
 * property read throws at runtime.
 *
 * `process.env.NEXT_PUBLIC_API_URL` is the one expression both bundlers can
 * inline — Next does it natively, and the Vite apps declare it in their own
 * `define` block (see `vite.config.ts`, "cross-app URLs"). Where neither
 * injects it, `process` is simply not defined in the browser, hence the
 * try/catch rather than a `typeof` guard: a `define` makes the same expression
 * a bare string literal, so the guard would be testing an identifier that no
 * longer appears in the emitted code.
 */
export function apiBaseUrl(): string {
  try {
    const injected = process.env.NEXT_PUBLIC_API_URL;
    if (injected) return trimTrailingSlash(injected);
  } catch {
    // No `process` in this runtime and no `define` — fall back to production.
  }
  return PROD_API_URL;
}

// ---------------------------------------------------------------------------
// Shape rules
// ---------------------------------------------------------------------------

/** Strip a trailing slash so `${base}${path}` never doubles up. */
export function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

/** Join an origin and a path with exactly one slash between them. */
export function withPath(base: string, path = ''): string {
  const root = trimTrailingSlash(base);
  if (!path) return root;
  return `${root}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * THE rule for addressing one workspace on the hosted app.
 *
 * Production serves each workspace from its own subdomain
 * (`acme.knitknot.io/reports/x`); local dev serves them all from one origin
 * and namespaces by path (`localhost:3701/acme/reports/x`, which the hosted
 * middleware passes straight through to the `/[workspace]/...` route).
 *
 * The base URL decides which form we emit — any `*.knitknot.io` base means
 * wildcard-subdomain hosting, anything else (localhost, a preview deploy) gets
 * the path form. Callers never branch on the environment themselves.
 */
export function workspaceUrl(base: string, slug: string, path = ''): string {
  const root = trimTrailingSlash(base);
  try {
    const { protocol, hostname } = new URL(root);
    if (hostname === 'knitknot.io' || hostname.endsWith('.knitknot.io')) {
      return withPath(`${protocol}//${slug}.knitknot.io`, path);
    }
  } catch {
    // Not parseable as an absolute URL — fall through to the path form.
  }
  return withPath(`${root}/${slug}`, path);
}

/**
 * The two halves of a workspace URL either side of the slug, for a UI that
 * renders the slug as an editable field between two fixed labels.
 *
 * Contract: `prefix + slug + suffix === workspaceUrl(base, slug)`. Derived
 * from `workspaceUrl` itself (never re-implemented) so an editor's affixes
 * can't drift from the link its copy button hands out.
 */
export function workspaceUrlAffixes(base: string): { prefix: string; suffix: string } {
  const PLACEHOLDER = ' slug ';
  const built = workspaceUrl(base, PLACEHOLDER);
  const at = built.indexOf(PLACEHOLDER);
  if (at === -1) return { prefix: trimTrailingSlash(base) + '/', suffix: '' };
  return { prefix: built.slice(0, at), suffix: built.slice(at + PLACEHOLDER.length) };
}

/** The same URL without its scheme, for display next to a copy button. */
export function displayUrl(url: string): string {
  return trimTrailingSlash(url).replace(/^https?:\/\//, '');
}
